import { useState, useEffect, useCallback, useRef } from 'react';
import { LobbyScreen } from '@/components/LobbyScreen';
import { SudokuBoard } from '@/components/SudokuBoard';
import { NumberPad } from '@/components/NumberPad';
import { GameHeader } from '@/components/GameHeader';
import { VictoryModal } from '@/components/VictoryModal';
import { GameOverModal } from '@/components/GameOverModal';
import { HowToPlayOverlay } from '@/components/HowToPlayOverlay';
import { PauseOverlay } from '@/components/PauseOverlay';
import { useSudokuGame } from '@/hooks/useSudokuGame';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { COLORS } from '@/game/configs';
import { type Difficulty } from '@/game/configs';
import { trackPlayByType, emitGameEvent } from '@/looplay';
type Screen = 'lobby' | 'howToPlay' | 'game';
interface BestTimes {
  easy: number | null;
  medium: number | null;
  hard: number | null;
}
export function HomePage() {
  const [currentScreen, setCurrentScreen] = useState<Screen>('lobby');
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [showGameOver, setShowGameOver] = useState(false);
  const [bestTimes, setBestTimes] = useLocalStorage<BestTimes>('sudoku-best-times', {
    easy: null,
    medium: null,
    hard: null,
  });
  const game = useSudokuGame();
  const hasTrackedWin = useRef(false);
  const hasTrackedLoss = useRef(false);
  const isNewBestTime = game.gameStatus === 'won' && (
    bestTimes[game.difficulty] === null ||
    game.timerSeconds < (bestTimes[game.difficulty] ?? Infinity)
  );
  useEffect(() => {
    if (game.gameStatus === 'won' && !hasTrackedWin.current) {
      hasTrackedWin.current = true;
      setShowVictory(true);
      if (
        bestTimes[game.difficulty] === null ||
        game.timerSeconds < bestTimes[game.difficulty]!
      ) {
        setBestTimes((prev) => ({
          ...prev,
          [game.difficulty]: game.timerSeconds,
        }));
      }
      trackPlayByType({
        trackingType: 'round',
        roundSeconds: game.timerSeconds,
      });
      emitGameEvent('puzzle_solved', {
        value: game.timerSeconds,
        payload: {
          difficulty: game.difficulty,
          mistakes: game.mistakes,
        },
      });
    }
    if (game.gameStatus === 'lost' && !hasTrackedLoss.current) {
      hasTrackedLoss.current = true;
      setShowGameOver(true);
      trackPlayByType({
        trackingType: 'round',
        roundSeconds: game.timerSeconds,
      });
    }
  }, [game.gameStatus, game.timerSeconds, game.difficulty, game.mistakes]);
  const handleStartGame = useCallback(() => {
    hasTrackedWin.current = false;
    hasTrackedLoss.current = false;
    setShowVictory(false);
    setShowGameOver(false);
    game.startGame();
    setCurrentScreen('game');
  }, [game]);
  const handleReturnToLobby = useCallback(() => {
    setShowVictory(false);
    setShowGameOver(false);
    game.resetGame();
    setCurrentScreen('lobby');
  }, [game]);
  const handlePlayAgain = useCallback(() => {
    hasTrackedWin.current = false;
    hasTrackedLoss.current = false;
    setShowVictory(false);
    setShowGameOver(false);
    game.startGame();
  }, [game]);
  const handleTryAgain = useCallback(() => {
    hasTrackedWin.current = false;
    hasTrackedLoss.current = false;
    setShowGameOver(false);
    game.startGame();
  }, [game]);
  const handlePause = useCallback(() => {
    if (game.gameStatus === 'playing') {
      game.pauseGame();
    } else if (game.gameStatus === 'paused') {
      game.resumeGame();
    }
  }, [game]);
  const handleNewGame = useCallback(() => {
    setShowVictory(false);
    setShowGameOver(false);
    game.startGame();
  }, [game]);
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (currentScreen !== 'game') return;
      if (game.gameStatus !== 'playing') return;
      const key = e.key;
      if (key >= '1' && key <= '9') {
        game.inputNumber(parseInt(key, 10));
      } else if (key === 'Backspace' || key === 'Delete') {
        game.erase();
      } else if (key === 'z' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        game.undo();
      } else if (key === 'n' || key === 'N') {
        game.toggleNote();
      } else if (key === 'Escape') {
        handlePause();
      }
    },
    [currentScreen, game, handlePause]
  );
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
  if (currentScreen === 'lobby') {
    return (
      <div
        style={{
          backgroundColor: COLORS.cream,
          minHeight: '100dvh',
        }}
      >
        <LobbyScreen
          onStartGame={handleStartGame}
          onHowToPlay={() => setShowHowToPlay(true)}
          difficulty={game.difficulty}
          onDifficultyChange={game.setDifficulty}
        />
        <HowToPlayOverlay
          isOpen={showHowToPlay}
          onClose={() => setShowHowToPlay(false)}
        />
      </div>
    );
  }
  return (
    <div
      className="flex flex-col min-h-[100dvh] px-3 py-4"
      style={{
        backgroundColor: COLORS.cream,
      }}
    >
      <div className="flex flex-col gap-4 flex-1">
        <div className="flex items-center justify-between">
          <button
            onClick={handleReturnToLobby}
            className="px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-150 active:scale-95"
            style={{
              backgroundColor: COLORS.ivory,
              color: COLORS.brown,
              boxShadow: '4px 4px 8px rgba(139, 115, 85, 0.2), -4px -4px 8px rgba(255, 255, 255, 0.9)',
            }}
          >
            Menu
          </button>
          <h1
            className="text-2xl font-extrabold"
            style={{ color: COLORS.dark }}
          >
            Sudoku
          </h1>
          <div className="w-16" />
        </div>
        <GameHeader
          difficulty={game.difficulty}
          timerSeconds={game.timerSeconds}
          mistakes={game.mistakes}
          isPaused={game.gameStatus === 'paused'}
          onPause={handlePause}
        />
        <div className="flex-1 flex items-center justify-center">
          <SudokuBoard
            board={game.board}
            selectedCell={game.selectedCell}
            onCellSelect={game.selectCell}
          />
        </div>
        <NumberPad
          notesMode={game.notesMode}
          onNumberPress={game.inputNumber}
          onToggleNotes={game.toggleNote}
          onErase={game.erase}
          onUndo={game.undo}
        />
      </div>
      <PauseOverlay
        isOpen={game.gameStatus === 'paused'}
        onResume={() => game.resumeGame()}
        onNewGame={handleNewGame}
        onReturnToLobby={handleReturnToLobby}
      />
      <VictoryModal
        isOpen={showVictory}
        time={game.timerSeconds}
        isNewBestTime={isNewBestTime}
        onPlayAgain={handlePlayAgain}
        onReturnToLobby={handleReturnToLobby}
      />
      <GameOverModal
        isOpen={showGameOver}
        time={game.timerSeconds}
        onTryAgain={handleTryAgain}
        onReturnToLobby={handleReturnToLobby}
      />
      <HowToPlayOverlay
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
      />
    </div>
  );
}